// packages
import cx from 'classnames';
import { get, set } from '../vendor/idb-keyval';
import { h } from 'preact';

// components
import CandidateRow from './CandidateRow';
import Contest from './Contest';
import Portal from './Portal';
import PureComponent from './PureComponent';
import Typeahead from './Typeahead';
import EmptyTable from './EmptyTable';

// internal
import debounce from '../utils/debounce';
import { determineContestTitleYourRaces } from '../election-utils';
import { visibleCandidatesCount } from '../endpoints';
import { lookup } from '../utils/legislative-districts';
import loadAssetURL from '../utils/load-asset-url';

const googleIMG = loadAssetURL('powered-by-google');

const sessionToken = new google.maps.places.AutocompleteSessionToken();
const autocompleteService = new google.maps.places.AutocompleteService();

const locationRestriction = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const componentRestrictions = { country: 'us' };
const types = ['geocode'];

const getPredictions = (input) => {
  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      { locationRestriction, componentRestrictions, input, sessionToken, types },
      (predict, status) => {
        if (status === 'OK') resolve(predict);

        reject(new Error('No results found.'));
      }
    );
  });
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

class YourRaces extends PureComponent {
  constructor() {
    super();

    this.state = {
      contests: [],
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      lookedFoundStored: false,
      results: [],
      value: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  onClear() {
    this.setState({ value: '' });
  }

  onChange(value) {
    this.setState({ value }, this.debouncedHandleGeocode);
  }

  handleGeocode() {
    const { value: input } = this.state;

    getPredictions(input)
      .then((results) => this.setState({ results }))
      .catch(() => this.setState({ results: [] }));
  }

  onSelect({ name, placeId }) {
    this.setState({ lookupError: false, lookupPending: true, value: name });

    this.placesService.getDetails(
      {
        fields: ['geometry.location'],
        placeId,
        sessionToken,
      },
      (result) => {
        const { lat: latitude, lng: longitude } =
          result.geometry.location.toJSON();

        const coordinates = { latitude, longitude };

        lookup(coordinates)
          .then(({ data }) =>
            this.setState(
              {
                coordinates,
                data,
                lookupPending: false,
                lookedFoundStored: false,
              },
              () => set('your-races-lookup', data)
            )
          )
          .catch(() =>
            this.setState({
              data: null,
              lookupError: true,
              lookupPending: false,
            })
          );
      }
    );
  }

  componentDidMount() {
    this.placesService = new google.maps.places.PlacesService(this.inputRef);

    get('your-races-lookup').then((data) => {
      if (data != null) {
        this.setState({ data, lookedFoundStored: true });
      }
    });
  }

  render() {
    const { contests, notes, annotations, asOf } = this.props;
    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      lookedFoundStored,
      results,
      value,
    } = this.state;

    let addressFeedback = null;

    let matches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const noteMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    const annoMatches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    let gridClass;

    if (data) {
      // Hacky fix for bug that let old races hang around after you search for an address in another district
      var arr = document.getElementsByClassName('representative-districts');
      for (var i = 0; i < arr.length; i++) {
        arr[i].innerHTML = ' ';
      }

      matches['us-house'] = contests.filter(
        (contest) =>
          contest.office === 'U.S. House' &&
          contest.office_division === data.us_house.district
      );

      matches['sboe'] = contests.filter(
        (contest) =>
          contest.office === 'State Board of Education' &&
          contest.office_division === data.sboe.district
      );

      matches['tx-senate'] = contests.filter(
        (contest) =>
          contest.office === 'Texas Senate' &&
          contest.office_division === data.tx_senate.district
      );

      matches['tx-house'] = contests.filter(
        (contest) =>
          contest.office === 'Texas House' &&
          contest.office_division === data.tx_house.district
      );

      noteMatches['us-house'] = notes.filter(
        (contest) =>
          contest.type === 'us-house-district-' + data.us_house.district
      );

      noteMatches['sboe'] = notes.filter(
        (contest) =>
          contest.type ===
          'state-board-of-education-district-' + data.sboe.district
      );

      noteMatches['tx-senate'] = notes.filter(
        (contest) =>
          contest.type === 'texas-senate-district-' + data.tx_senate.district
      );

      noteMatches['tx-house'] = notes.filter(
        (contest) =>
          contest.type === 'texas-house-district-' + data.tx_house.district
      );

      annoMatches['us-house'] = annotations.filter(
        (contest) =>
          contest.type === 'us-house-district-' + data.us_house.district
      );

      annoMatches['sboe'] = annotations.filter(
        (contest) =>
          contest.type ===
          'state-board-of-education-district-' + data.sboe.district
      );

      annoMatches['tx-senate'] = annotations.filter(
        (contest) =>
          contest.type === 'texas-senate-district-' + data.tx_senate.district
      );

      annoMatches['tx-house'] = annotations.filter(
        (contest) =>
          contest.type === 'texas-house-district-' + data.tx_house.district
      );

      let arrayNum;
      arrayNum =
        matches['us-house'].length +
        matches['tx-house'].length +
        matches['sboe'].length +
        matches['tx-senate'].length;

      gridClass = cx('representative-districts contest-list', {
        'seat__contests-double': arrayNum > 1,
        'seat__contests-single': arrayNum == 1,
      });
    }

    if (lookupPending) {
      addressFeedback = (
        <p class='has-padding t-serif t-weight-bold t-align-center'>
          Finding your details...{' '}
          <svg class='icon icon-spinner animate-pulse'>
            <use xlinkHref='#icon-spinner' />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class='has-padding t-serif t-weight-bold t-align-center'>
          Hmm... we were unable to find you in Texas. Please check your address
          and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class='has-padding t-serif t-weight-bold t-align-center'>
          We found your information! These races have been personalized for you.
        </p>
      );
    }

    if (lookedFoundStored && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class='has-padding t-serif t-weight-bold t-align-center'>
          Welcome back! We remembered the districts you found last time.
        </p>
      );
      if (
        (coordinates || lookedFoundStored) &&
        matches['us-house'].length == 0 &&
        matches['tx-house'].length == 0 &&
        matches['sboe'].length == 0 &&
        matches['tx-senate'].length == 0
      ) {
        addressFeedback = (
          <p class='has-padding t-serif t-weight-bold t-align-center'>
            Welcome back! We remembered the districts you found last time.{' '}
            <br></br> <br></br>
            We're fetching the races for your district.
            <svg class='icon icon-spinner animate-pulse'>
              <use xlinkHref='#icon-spinner' />
            </svg>
          </p>
        );
      }
    }

    const inputProps = {
      id: 'address-input',
      class: 'address-input__input',
      placeholder: '1010 Colorado St, Austin, TX, 78701',
    };

    function findIsCalled(party, matches) {
      const race = matches.filter((race) => race.party === party);
      return race[0].is_called;
    }

    function findContestResult(party, matches) {
      const race = matches.filter((race) => race.party === party);
      return race[0].contest_result;
    }

    return (
      <div class='address-input t-sans'>
        <svg class='icon icon-drawn-left-arrow'>
          <use xlinkHref='#icon-drawn-left-arrow'></use>
        </svg>
        <label class='address-input__label' for='address-input'>
          Enter your address
        </label>
        <Typeahead
          inputProps={inputProps}
          items={results}
          getItemValue={(item) => ({
            name: item.description,
            placeId: item.place_id,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class='listbox__item listbox__item--attribution'>
              <img src={googleIMG} alt='Powered by Google' />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={(props) => (
            <input
              {...props}
              ref={(el) => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        {addressFeedback}
        {data && contests.length > 0 && (
          <Portal
            node={document.getElementById('your-races-results')}
            ref={(ref) => (this._resultsPortal = ref)}
          >
            <div class={gridClass}>
              {matches['us-house'].length > 0 &&
                matches['us-house'].map((contest) => (
                  <Contest
                    key={matches['us-house'][0].office_slug}
                    contest={contest}
                    contestTitle={determineContestTitleYourRaces(
                      matches['us-house'][0]
                    )}
                    tableHeader={CandidateRow.Header}
                    columnCount={CandidateRow.columnCount}
                    annotations={annoMatches['us-house'].filter(
                      (annotation) => annotation.type === contest.office_slug
                    )}
                    notes={noteMatches['us-house'].filter(
                      (note) => note.type === contest.office_slug
                    )}
                    source={contest.source}
                    asOf={asOf}
                    renderCandidate={(
                      candidate,
                      idx,
                      viewAllCandidates,
                      { totalVotes }
                    ) => (
                      <CandidateRow
                        ballotName={candidate.name}
                        candidateId={candidate.id}
                        contestOffice={matches['us-house'][0].office}
                        contestIsCalled={matches['us-house'][0].is_called}
                        contestResult={matches['us-house'][0].contest_result}
                        candidatesNum={
                          matches['us-house'][0].candidacies.length
                        }
                        isIncumbent={candidate.is_incumbent}
                        isWinner={candidate.is_winner}
                        isViewable={
                          viewAllCandidates || idx < visibleCandidatesCount
                        }
                        party={candidate.party}
                        totalVotesInContest={totalVotes}
                        votes={candidate.votes}
                      />
                    )}
                  />
                ))}
              {matches['tx-senate'].length > 0 ? (
                matches['tx-senate'].map((contest) => (
                  <Contest
                    key={matches['tx-senate'].id}
                    contest={contest}
                    tableHeader={CandidateRow.Header}
                    columnCount={CandidateRow.columnCount}
                    contestTitle={determineContestTitleYourRaces(
                      matches['tx-senate'][0]
                    )}
                    annotations={annoMatches['tx-senate'].filter(
                      (annotation) => annotation.type === contest.office_slug
                    )}
                    notes={noteMatches['tx-senate'].filter(
                      (note) => note.type === contest.office_slug
                    )}
                    source={contest.source}
                    asOf={asOf}
                    renderCandidate={(
                      candidate,
                      idx,
                      viewAllCandidates,
                      { totalVotes }
                    ) => (
                      <CandidateRow
                        ballotName={candidate.name}
                        contestOffice={matches['tx-senate'][0].office}
                        contestIsCalled={matches['tx-senate'][0].is_called}
                        contestResult={matches['tx-senate'][0].contest_result}
                        candidatesNum={
                          matches['tx-senate'][0].candidacies.length
                        }
                        isIncumbent={candidate.is_incumbent}
                        isWinner={candidate.is_winner}
                        isViewable={
                          viewAllCandidates || idx < visibleCandidatesCount
                        }
                        party={candidate.party}
                        totalVotesInContest={totalVotes}
                        votes={candidate.votes}
                      />
                    )}
                  />
                ))) :
                <EmptyTable
                  slug={'texas-senate'}
                  contestTitle={'Texas Senate'}
                  contestNum={data['tx_senate']['district']}
                  tableHeader={CandidateRow.Header}
                />}
              {matches['tx-house'].length > 0 &&
                matches['tx-house'].map((contest) => (
                  <Contest
                    key={matches['tx-house'].id}
                    contest={contest}
                    tableHeader={CandidateRow.Header}
                    columnCount={CandidateRow.columnCount}
                    contestTitle={determineContestTitleYourRaces(
                      matches['tx-house'][0]
                    )}
                    annotations={annoMatches['tx-house'].filter(
                      (annotation) => annotation.type === contest.office_slug
                    )}
                    notes={noteMatches['tx-house'].filter(
                      (note) => note.type === contest.office_slug
                    )}
                    source={contest.source}
                    asOf={asOf}
                    renderCandidate={(
                      candidate,
                      idx,
                      viewAllCandidates,
                      { totalVotes }
                    ) => (
                      <CandidateRow
                        ballotName={candidate.name}
                        contestOffice={matches['tx-house'][0].office}
                        contestIsCalled={matches['tx-house'][0].is_called}
                        contestResult={matches['tx-house'][0].contest_result}
                        candidatesNum={
                          matches['tx-house'][0].candidacies.length
                        }
                        isIncumbent={candidate.is_incumbent}
                        isViewable={
                          viewAllCandidates || idx < visibleCandidatesCount
                        }
                        isWinner={candidate.is_winner}
                        party={candidate.party}
                        totalVotesInContest={totalVotes}
                        votes={candidate.votes}
                      />
                    )}
                  />
                ))}
              {matches['sboe'].length > 0 ? (
                matches['sboe'].map((contest) => (
                  <Contest
                    key={matches['sboe'][0].office_slug}
                    contest={contest}
                    tableHeader={CandidateRow.Header}
                    columnCount={CandidateRow.columnCount}
                    contestTitle={determineContestTitleYourRaces(
                      matches['sboe'][0]
                    )}
                    annotations={annoMatches['sboe'].filter(
                      (annotation) => annotation.type === contest.office_slug
                    )}
                    notes={noteMatches['sboe'].filter(
                      (note) => note.type === contest.office_slug
                    )}
                    source={contest.source}
                    asOf={asOf}
                    renderCandidate={(
                      candidate,
                      idx,
                      viewAllCandidates,
                      { totalVotes }
                    ) => (
                      <CandidateRow
                        ballotName={candidate.name}
                        contestOffice={matches['sboe'][0].office}
                        contestIsCalled={matches['sboe'][0].is_called}
                        contestResult={matches['sboe'][0].contest_result}
                        candidatesNum={matches['sboe'][0].candidacies.length}
                        isIncumbent={candidate.is_incumbent}
                        isWinner={candidate.is_winner}
                        isViewable={
                          viewAllCandidates || idx < visibleCandidatesCount
                        }
                        party={candidate.party}
                        totalVotesInContest={totalVotes}
                        votes={candidate.votes}
                      />
                    )}
                  />
                ))) :
                <EmptyTable
                  slug={'state-board-of-education'}
                  contestTitle={'State Board of Education'}
                  contestNum={data['sboe']['district']}
                  tableHeader={CandidateRow.Header}
                />
              }
            </div>
          </Portal>
        )}
      </div>
    );
  }
}

export default YourRaces;